import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { IconHome } from "../../icons/IconHome";

import iconCollapsedMenu from "./../../assets/icon_colappsedMenu.svg";

import { IconScheduler } from "../../icons/IconScheduler";
import { IconReport } from "../../icons/IconReport";
import { IconProject } from "../../icons/IconProject";
import { IconClient } from "../../icons/IconClient";
import { IconCollaborators } from "../../icons/IconCollaborators";
import LogoutIcon from "@mui/icons-material/Logout";
import usePermission from "../../hooks/usePermission";
import { FeatureStatic } from "../../@types/statics/feature.static";
import { IconConfigurations } from "../../icons/IconConfigurations";
import { TenantSelect } from "../TenantSelect";
import { useKeycloak } from "@react-keycloak/web";
import { IKeyText } from "../../@types/key-value.type";
import { TUserInfo } from "../../@types/user-info.type";
import { getAllTenantsByAccount } from "../../services/api/tenant/TenantService";
import { ITenant } from "../../@types/tenant.type";
import styles from "./styles.module.scss";
import { IconUser } from "./icons/IconMyProfile";
import { storageUserGet } from "../../storage/storageUser";
import { getConfigurations } from "../../utils/checkConfigurations";
import { IDefault } from "../../@types/default.type";

export const useTenants = () => {
  const { keycloak, initialized } = useKeycloak();
  const [tenants, setTenants] = useState<IKeyText[]>([]);

  useEffect(() => {
    const loadTenants = async () => {
      if (!initialized || !keycloak.authenticated) return;

      const user = (await keycloak.loadUserInfo()) as TUserInfo;
      const tenantsData = await getAllTenantsByAccount(user.email);
      const options = tenantsData?.map((tenant: ITenant) => ({
        key: tenant.id,
        text: tenant.tenantName,
      })) as IKeyText[];
      setTenants(options);
    };

    loadTenants();
  }, [initialized, keycloak]);

  return tenants;
};

interface MenuComponentProps {
  setToggleMenuMobile: (value: boolean) => void;
  toggledMenuMobile: boolean;
}

export const MenuComponent: React.FC<MenuComponentProps> = ({
  setToggleMenuMobile,
  toggledMenuMobile,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const tenants = useTenants();
  const { keycloak } = useKeycloak();
  const [broken, setBroken] = useState(false);
  const [canSeeConfig, setCanSeeConfig] = useState<boolean>(false);
  const permissions = usePermission();
  const user = storageUserGet();

  const location = useLocation();

  const pathAtual = location.pathname;

  useEffect(() => {
    const itemsConfig = getConfigurations();

    for (let index = 0; index < itemsConfig.length; index++) {
      const element = itemsConfig[index];
      const feature = FeatureStatic[element.featureKey] as IDefault;
      if(permissions.canView(feature.id)){
        setCanSeeConfig(true);
        break;
      }
    }
  }, [permissions]);
 
  return (
    <div className={`${styles.aside} no-print`}>
      <button
        className={styles.btnCollapsed}
        onClick={() => {
          setCollapsed(!collapsed);
        }}
      >
        {pathAtual === "/scheduler" || pathAtual === "/reports" ? (
          <img
            src={iconCollapsedMenu}
            alt=""
            style={{ cursor: "not-allowed" }}
          />
        ) : collapsed ? (
          <img src={iconCollapsedMenu} alt="" />
        ) : (
          <img src={iconCollapsedMenu} className={styles.rotateImg} alt="" />
        )}
      </button>
      <Sidebar
        style={{ height: "100%" }}
        collapsed={
          pathAtual === "/scheduler" || pathAtual === "/reports" || collapsed
        }
        toggled={toggledMenuMobile}
        onBackdropClick={() => setToggleMenuMobile(false)}
        onBreakPoint={setBroken}
        breakPoint="md"
      >
        <Menu className={styles.menu}>
          <MenuItem
            title={collapsed ? "Página Inicial" : ""}
            icon={<IconHome />}
            component={<Link to="/" />}
            className={`${styles.itemMenu} ${
              pathAtual === "/" && styles.active
            }`}
          >
            Página Inicial
          </MenuItem>
          {permissions.canView(FeatureStatic.allocationMap.id) && (
            <MenuItem
              title={collapsed ? "Mapa de Alocação" : ""}
              icon={<IconScheduler />}
              component={<Link to="/scheduler" />}
              className={`${styles.itemMenu} ${styles.schedulerItem} ${
                pathAtual === "/scheduler" && styles.active
              }`}
            >
              Mapa de Alocação
            </MenuItem>
          )}
          {permissions.canView(FeatureStatic.reports.id) && (
            <MenuItem
              title={collapsed ? "Reports" : ""}
              icon={<IconReport />}
              component={<Link to="/reports" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("reports") && styles.active
              }`}
            >
              Relatórios
            </MenuItem>
          )}
          {permissions.canView(FeatureStatic.projects.id) && (
            <MenuItem
              title={collapsed ? "Projetos" : ""}
              icon={<IconProject />}
              component={<Link to="/projects" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("projects") &&
                !pathAtual.includes("configurations") &&
                styles.active
              } `}
            >
              Projetos
            </MenuItem>
          )}
          {permissions.canView(FeatureStatic.clients.id) && (
            <MenuItem
              title={collapsed ? "Clientes" : ""}
              icon={<IconClient />}
              component={<Link to="/customers" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("customers") && styles.active
              }`}
            >
              Clientes
            </MenuItem>
          )}
          {permissions.canView(FeatureStatic.resources.id) && (
            <MenuItem
              title={collapsed ? "Colaboradores" : ""}
              icon={<IconCollaborators />}
              component={<Link to="/resources" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("resources") && styles.active
              }`}
            >
              Colaboradores
            </MenuItem>
          )}

          <div className={styles.tenantSelect}>
            {tenants.length > 1 && <TenantSelect />}
          </div>
          {
            user.resourceId &&
            <MenuItem
              title={collapsed ? "Meu Perfil" : ""}
              icon={<IconUser />}
              component={<Link to="/user-profile" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("user-profile") && styles.active
              }`}
            >
              Meu Perfil
            </MenuItem>
          }

          {
            canSeeConfig &&
            <MenuItem
              title={collapsed ? "Configurações" : ""}
              icon={<IconConfigurations />}
              component={<Link to="/configurations" />}
              className={`${styles.itemMenu} ${
                pathAtual.includes("configurations") && styles.active
              }`}
            >
              Configurações
            </MenuItem>
          }

          <MenuItem
            title={collapsed ? "Sair" : ""}
            icon={<LogoutIcon />}
            onClick={() => keycloak.logout()}
            className={`${styles.itemMenu} ${
              pathAtual.includes("sair") && styles.active
            }`}
          >
            Sair
          </MenuItem>
        </Menu>
      </Sidebar>
    </div>
  );
};
